import { makeStyles } from '@material-ui/core/styles';
import React, { useState, useEffect } from 'react';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AttachMoneyRoundedIcon from '@material-ui/icons/AttachMoneyRounded';
import AppsRoundedIcon from '@material-ui/icons/AppsRounded';
import EmojiObjectsOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined';
import LocalMallOutlinedIcon from '@material-ui/icons/LocalMallOutlined';

import { member as v_member, basic, Values } from '../../../request/values';
import { COLORS } from '../../../shared/Theme';
import { TitleBackBar } from '../../../shared/header/TitleBar';
import { formatMoney } from '../../../locales/formatModule';

const SERVER = basic.server;

const THEME = {
	BACKGROUND: '#f4f7fa',
	SURFACE: '#ffffff',
	TEXT_MAIN: '#1f2933',
	TEXT_SUB: '#8a94a6',
	BORDER: '#e8edf2',
	ROW_BORDER: '#eef2f6',
	WATER_BLUE: COLORS.WATER_BLUE,
	WATER_BLUE_DARK: '#0e7ab8',
	WATER_BLUE_LIGHT: '#3aa3e3',
};

const useStyles = makeStyles((theme) => ({
	page: {
		minHeight: '100%',
		backgroundColor: THEME.BACKGROUND,
		paddingBottom: theme.spacing(4),
	},
	listWrapper: {
		padding: '16px 16px 24px',
		maxWidth: 560,
		margin: '0 auto',
		[theme.breakpoints.up('md')]: {
			padding: '24px 28px 32px',
		},
	},
	summaryCard: {
		position: 'relative',
		overflow: 'hidden',
		borderRadius: 22,
		padding: '22px 20px 18px',
		color: THEME.SURFACE,
		background: `linear-gradient(165deg, ${THEME.WATER_BLUE_LIGHT} 0%, ${THEME.WATER_BLUE} 48%, ${THEME.WATER_BLUE_DARK} 100%)`,
		boxShadow: '0 10px 28px rgba(18, 145, 217, 0.28)',
		marginBottom: 14,
		'&::before': {
			content: '""',
			position: 'absolute',
			top: -48,
			right: -36,
			width: 140,
			height: 140,
			borderRadius: '50%',
			background: 'rgba(255,255,255,0.12)',
		},
		'&::after': {
			content: '""',
			position: 'absolute',
			bottom: -56,
			left: -28,
			width: 120,
			height: 120,
			borderRadius: '50%',
			background: 'rgba(255,255,255,0.08)',
		},
	},
	summaryHeader: {
		position: 'relative',
		zIndex: 1,
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: 10,
		marginBottom: 6,
		flexWrap: 'wrap',
	},
	summaryLabel: {
		fontSize: '1.05rem',
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		letterSpacing: '-0.02em',
		lineHeight: 1.3,
	},
	summaryHint: {
		fontSize: '0.72rem',
		fontFamily: 'NanumR',
		opacity: 0.88,
		lineHeight: 1.3,
		whiteSpace: 'nowrap',
	},
	summaryAmount: {
		position: 'relative',
		zIndex: 1,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		fontSize: '1.85rem',
		letterSpacing: '-0.03em',
		lineHeight: 1.2,
		marginBottom: 16,
		[theme.breakpoints.up('md')]: {
			fontSize: '2.1rem',
		},
	},
	summaryStats: {
		position: 'relative',
		zIndex: 1,
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 10,
	},
	summaryStat: {
		backgroundColor: 'rgba(255,255,255,0.14)',
		border: '1px solid rgba(255,255,255,0.18)',
		borderRadius: 14,
		padding: '10px 12px',
	},
	summaryStatLabel: {
		fontSize: '0.72rem',
		fontFamily: 'NanumR',
		opacity: 0.88,
		marginBottom: 4,
	},
	summaryStatValue: {
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		fontSize: '0.95rem',
		letterSpacing: '-0.02em',
	},
	detailCard: {
		backgroundColor: THEME.SURFACE,
		borderRadius: 22,
		border: `1px solid ${THEME.BORDER}`,
		boxShadow: '0 4px 18px rgba(31, 41, 51, 0.05)',
		padding: '8px 4px',
		overflow: 'hidden',
	},
	section: {
		padding: '10px 14px 4px',
	},
	sectionTitle: {
		fontSize: '0.78rem',
		fontFamily: 'NanumR',
		color: THEME.TEXT_SUB,
		marginBottom: 2,
		paddingLeft: 4,
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
		padding: '14px 4px',
		borderBottom: `1px solid ${THEME.ROW_BORDER}`,
		'&:last-child': {
			borderBottom: 'none',
		},
	},
	rowLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		minWidth: 0,
	},
	iconWrap: {
		width: 34,
		height: 34,
		borderRadius: 11,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		'& svg': {
			fontSize: 18,
		},
	},
	iconCash: {
		backgroundColor: 'rgba(18, 145, 217, 0.12)',
		color: THEME.WATER_BLUE,
	},
	iconInventory: {
		backgroundColor: '#eef1f5',
		color: '#5b6777',
	},
	iconIp: {
		backgroundColor: 'rgba(58, 163, 227, 0.14)',
		color: THEME.WATER_BLUE_LIGHT,
	},
	iconSold: {
		backgroundColor: 'rgba(214, 69, 69, 0.1)',
		color: COLORS.RED,
	},
	iconDebt: {
		width: 34,
		height: 34,
		borderRadius: 11,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: '#f3f5f8',
		flexShrink: 0,
	},
	debtDot: {
		width: 8,
		height: 8,
		borderRadius: '50%',
		backgroundColor: '#b7c0cc',
	},
	rowLabel: {
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		fontSize: '0.95rem',
		color: THEME.TEXT_MAIN,
		letterSpacing: '-0.01em',
	},
	rowValue: {
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		fontSize: '0.95rem',
		color: THEME.TEXT_MAIN,
		letterSpacing: '-0.02em',
		whiteSpace: 'nowrap',
	},
	rowValueDebt: {
		color: COLORS.RED,
	},
	rowValueDebtMuted: {
		color: '#e88a8a',
		fontFamily: 'NanumR',
		fontWeight: 'normal',
	},
	sectionDivider: {
		height: 1,
		backgroundColor: THEME.ROW_BORDER,
		margin: '4px 14px 0',
	},
	status: {
		minHeight: 260,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 12,
		textAlign: 'center',
		color: THEME.TEXT_SUB,
	},
	retryButton: {
		backgroundColor: THEME.WATER_BLUE,
		color: THEME.SURFACE,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		textTransform: 'none',
		'&:hover': { backgroundColor: THEME.WATER_BLUE_DARK },
	},
}));

type ListRow = {
	key: string;
	label: string;
	value: number;
	icon: React.ReactNode | null;
	iconClass?: string;
	isDebt?: boolean;
};

const CompanyAsset: React.FC<any> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation(['student', 'common']);
	const { member } = props;
	const history = useHistory();

	const [asset, setAsset] = useState({
		company_name: '',
		stock: 0,
		loan: 0,
		loan_interest: 0,
		capital: 0,
		license_stock: 0,
		material_stock: 0,
		material_loan_stock: 0,
	});
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);

	const normalizeAsset = (data: any) => ({
		company_name: String(data?.company_name || ''),
		stock: Number(data?.stock) || 0,
		loan: Number(data?.loan) || 0,
		loan_interest: Number(data?.loan_interest) || 0,
		capital: Number(data?.capital) || 0,
		license_stock: Number(data?.license_stock) || 0,
		material_stock: Number(data?.material_stock) || 0,
		material_loan_stock: Number(data?.material_loan_stock) || 0,
	});

	const getCompanyAsset = () => {
		setLoading(true);
		setLoadError(false);
		const url = new URL(SERVER + v_member.member + v_member.company_asset);
		const params = {
			companyNum: member.companyNum,
			classVersion: member.classVersion,
		};
		url.search = new URLSearchParams(params).toString();

		return axios({
			method: 'GET',
			withCredentials: true,
			url: url.toString(),
		})
			.then((res) => res.data)
			.then((data) => {
				if (data.code === Values.SUCCESS_CODE && data.asset) {
					setAsset(normalizeAsset(data.asset));
					return;
				}
				setLoadError(true);
			})
			.catch(() => setLoadError(true))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		getCompanyAsset();
	}, []);

	const isManufacture = member.companyType === 'M';
	const capitalTotal = asset.capital + asset.license_stock;

	const assetRows: ListRow[] = [
		{
			key: 'cash',
			label: t('CompanyAsset.cashAsset'),
			value: asset.stock,
			icon: <AttachMoneyRoundedIcon />,
			iconClass: classes.iconCash,
		},
		{
			key: 'inventory',
			label: t('CompanyAsset.inventoryAsset'),
			value: asset.material_stock,
			icon: <AppsRoundedIcon />,
			iconClass: classes.iconInventory,
		},
	];

	if (isManufacture) {
		assetRows.push({
			key: 'ip',
			label: t('CompanyAsset.intellectualProperty'),
			value: asset.license_stock,
			icon: <EmojiObjectsOutlinedIcon />,
			iconClass: classes.iconIp,
		});
	}

	const debtRows: ListRow[] = [
		{ key: 'loan', label: t('CompanyAsset.loan'), value: asset.loan, icon: null, isDebt: true },
		{ key: 'loanInterest', label: t('CompanyAsset.loanInterest'), value: asset.loan_interest, icon: null, isDebt: true },
	];

	if (!isManufacture) {
		debtRows.push({
			key: 'soldMaterials',
			label: t('CompanyAsset.soldMaterials'),
			value: asset.material_loan_stock,
			icon: <LocalMallOutlinedIcon />,
			iconClass: classes.iconSold,
			isDebt: true,
		});
	}

	const totalAssets = assetRows.reduce((sum, row) => sum + row.value, 0);
	const totalLiabilities = debtRows.reduce((sum, row) => sum + row.value, 0);

	const renderDebtIcon = (row: ListRow) => {
		if (row.icon && row.iconClass) {
			return <div className={`${classes.iconWrap} ${row.iconClass}`}>{row.icon}</div>;
		}
		return (
			<div className={classes.iconDebt}>
				<span className={classes.debtDot} />
			</div>
		);
	};

	return (
		<div className={`content ${classes.page}`}>
			<TitleBackBar history={history} title={t('CompanyAsset.title')} titleIcon="price" />
			<Box className={classes.listWrapper}>
				{loading ? (
					<div className={classes.status} data-testid="company-asset-loading">
						<CircularProgress size={32} />
						<Typography>{t('CompanyAsset.loading')}</Typography>
					</div>
				) : loadError ? (
					<div className={classes.status} data-testid="company-asset-error">
						<Typography>{t('CompanyAsset.loadError')}</Typography>
						<Button className={classes.retryButton} onClick={getCompanyAsset}>
							{t('CompanyAsset.retry')}
						</Button>
					</div>
				) : (
					<>
				<div className={classes.summaryCard}>
					<div className={classes.summaryHeader}>
						<Typography className={classes.summaryLabel}>{t('CompanyAsset.capitalStats')}</Typography>
						<Typography className={classes.summaryHint}>{t('CompanyAsset.capitalHint')}</Typography>
					</div>
					<Typography className={classes.summaryAmount} data-testid="company-asset-capital">
						{formatMoney(capitalTotal)}
					</Typography>

					<div className={classes.summaryStats}>
						<div className={classes.summaryStat}>
							<Typography className={classes.summaryStatLabel}>{t('CompanyAsset.totalAssets')}</Typography>
							<Typography className={classes.summaryStatValue}>{formatMoney(totalAssets)}</Typography>
						</div>
						<div className={classes.summaryStat}>
							<Typography className={classes.summaryStatLabel}>{t('CompanyAsset.totalLiabilities')}</Typography>
							<Typography className={classes.summaryStatValue}>{formatMoney(totalLiabilities)}</Typography>
						</div>
					</div>
				</div>

				<div className={classes.detailCard}>
					<section className={classes.section}>
						<Typography className={classes.sectionTitle}>{t('CompanyAsset.assets')}</Typography>
						{assetRows.map((row) => (
							<div key={row.key} className={classes.row}>
								<div className={classes.rowLeft}>
									<div className={`${classes.iconWrap} ${row.iconClass}`}>{row.icon}</div>
									<Typography className={classes.rowLabel}>{row.label}</Typography>
								</div>
								<Typography className={classes.rowValue}>{formatMoney(row.value)}</Typography>
							</div>
						))}
					</section>

					<div className={classes.sectionDivider} />

					<section className={classes.section}>
						<Typography className={classes.sectionTitle}>{t('CompanyAsset.liabilities')}</Typography>
						{debtRows.map((row) => (
							<div key={row.key} className={classes.row}>
								<div className={classes.rowLeft}>
									{renderDebtIcon(row)}
									<Typography className={classes.rowLabel}>{row.label}</Typography>
								</div>
								<Typography
									className={`${classes.rowValue} ${
										row.value === 0 ? classes.rowValueDebtMuted : classes.rowValueDebt
									}`}
								>
									{formatMoney(row.value)}
								</Typography>
							</div>
						))}
					</section>
				</div>
					</>
				)}
			</Box>
		</div>
	);
};

export default CompanyAsset;
